import Head from "next/head";
import { GetServerSidePropsContext } from "next";
import { styled } from "@mui/material";
import { useEffect, useMemo } from "react";
import { useTranslation } from "../../helpers/i18n";
import { useQueryParams } from "../../dataAccess/QueryParams";
import { useAnalyticsTracker } from "../../app/AnalyticsTracker";
import CommonHead from "../../builder/CommonHead";
import { PageSettings } from "../../builder/PageConfiguration";
import { getComponentSettings, getGridHeaders } from "../../builder/Theme";
import {
  getCollections,
  useCollections,
} from "../../dataAccess/api/collections";
import { getPartners, usePartners } from "../../dataAccess/api/partners";
import { getCategories, useCategories } from "../../dataAccess/api/categories";
import {
  Configuration,
  useConfiguration,
} from "../../dataAccess/api/configuration";
import { Layout } from "../../builder/Layout";
import { getItems, getSections } from "../../dataAccess/api/items";
import BannerWithLogo from "../public/BannerWithLogo";
import BannerWithTextCustomHTML from "../public/BannerWithTextCustomHTML";
import BannerManageProfileSignUp from "../public/BannerManageProfileSignUp";
import BannerWithTextWithImage from "../public/BannerWithTextWithImage";
import CTAButton from "../public/CTAButton";
import SkipToMainContentAnchor from "../public/SkipToMainContentAnchor";
import Header from "../public/Header";
import Text from "../public/Text";
import Search from "../public/Search";
import ManageProfileSignUpButton from "../public/ManageProfileSignUpButton";
import CollectionsFilter from "../public/CollectionsFilter";
import PartnersFilter from "../public/PartnersFilter";
import CategoriesFilter from "../public/CategoriesFilter";
import { Grid, GridSettings, getTilesPerSection } from "../public/Grid";
import Profile, { ProfileContext, useProfileState } from "../public/Profile";
import { useScreenshotPreviewElementEffect } from "../../helpers/environment";
import PoweredByBuiltfirstBanner from "../ui/PoweredByBuiltfirstBanner";
import Page from "../ui/Page";
import { HTTPClientV3 } from "../../dataAccess/HTTPClientV3";
import { useApiClientContext } from "../../app/ApiClientCtx";

export const getServerSideProps = async (
  context: GetServerSidePropsContext,
  configuration: Configuration,
) => {
  const APIV3 = new HTTPClientV3(
    context.req.headers.host || null,
    context.resolvedUrl || null,
    null,
  );
  const services = [
    getPartners(APIV3),
    getCollections(APIV3),
    getCategories(APIV3),
  ];
  const gridSettings = getComponentSettings<GridSettings>(
    configuration,
    "PLP",
    "Grid",
  );
  getSections(context.query, getGridHeaders(configuration)).forEach(
    (section, index) =>
      services.push(
        getItems(
          APIV3,
          context.query,
          section,
          index + 1,
          getTilesPerSection(
            gridSettings.sectionTileDensity,
            gridSettings.maxNumberOfColumnsOnDesktop,
          ),
        ),
      ),
  );

  const results = await Promise.all(services.map((service) => service.json()));
  const fallback: any = {};
  results.forEach((data, index) => {
    fallback[services[index].fallbackKey] = data;
  });

  return {
    props: {
      resourceNotFound: false,
      fallback,
    },
  };
};

/**
 * this imports are not dynamic to reduce bundle number of files:
 */
export const availableComponents = {
  Grid,
  BannerWithLogo,
  BannerWithTextCustomHTML,
  ManageProfileSignUpButton,
  BannerWithTextWithImage,
  BannerManageProfileSignUp,
  CTAButton,
  SkipToMainContentAnchor,
  Header,
  Text,
  Search,
  PartnersFilter,
  CategoriesFilter,
  CollectionsFilter,
  Profile,
};

export type AvailableComponents = typeof availableComponents;

export const MainContainer = styled("div")(() => ({}));

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const PLP = ({ settings }: Required<{ settings: PageSettings }>) => {
  const { t } = useTranslation();
  const { partnerList } = usePartners();
  const { collectionList } = useCollections();
  const { categoryList } = useCategories();
  const profileState = useProfileState();
  const { q } = useQueryParams();
  const { configuration } = useConfiguration();
  const { AnalyticsTracker } = useAnalyticsTracker();
  const { APIV3 } = useApiClientContext();

  useScreenshotPreviewElementEffect();

  useEffect(() => {
    AnalyticsTracker.trackIfItemSearch(APIV3, { searchTerm: q });
  }, [q]);

  const componentProps = useMemo(
    () => ({
      isBackButtonIn: false,
      backButtonIn: undefined,
      partnerList: partnerList?.data,
      categories: categoryList?.data,
      collections: collectionList?.data,
      mobile: undefined,
      navigation: undefined,
      item: undefined,
      homePageLogo: configuration?.homePageLogoUrl,
      marketplaceName: configuration?.name,
      ref: undefined,
      key: undefined,
    }),
    [
      categoryList?.data,
      collectionList?.data,
      partnerList?.data,
      configuration?.homePageLogoUrl,
      configuration?.name,
    ],
  );

  const skipToMainContentAvailable = useMemo(() => {
    const pageConfiguration =
      configuration?.publishedTemplate.properties.pages.find(
        ({ id }: { id: string }) => id === "PLP",
      );
    return !!pageConfiguration?.components?.find(
      ({ type }) => type === "SkipToMainContentAnchor",
    );
  }, [configuration?.publishedTemplate]);

  return (
    <Page skipToMainContentAvailable={skipToMainContentAvailable}>
      <MainContainer id="app-content">
        <CommonHead />
        <Head>
          <meta name="description" content={t("seo_plp_meta_description")} />
        </Head>
        <ProfileContext.Provider value={profileState}>
          <Layout
            availableComponents={availableComponents}
            componentProps={componentProps}
          />
          <PoweredByBuiltfirstBanner />
        </ProfileContext.Provider>
      </MainContainer>
    </Page>
  );
};

export default PLP;
